/**
 * Logger utility that replaces console.log with environment-aware logging
 * In production, it will not log anything to avoid performance impact and security concerns
 */

class Logger {
  constructor() {
    this.config = void 0;
    // Determine the environment - only one can be true at a time
    const nodeEnv = process.env.NODE_ENV;
    console.log("🔄 Node environment:", nodeEnv);
    if (nodeEnv === 'production') {
      this.config = {
        environment: 'production'
      };
    } else if (nodeEnv === 'test') {
      this.config = {
        environment: 'test'
      };
    } else {
      // Default to development for any other value (including undefined)
      this.config = {
        environment: 'development'
      };
    }
  }
  shouldLog(level) {
    // Adjusted to be the same as next.config.js
    if (this.config.environment === 'production') {
      return level === 'error' || level === 'warn';
    }

    // In development, log everything
    return this.config.environment === 'development';
  }
  safeStringify(value) {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return '[Object]';
      }
    }
    return String(value);
  }
  formatMessage(level, message) {
    const timestamp = new Date().toISOString();
    const prefix = "[".concat(timestamp, "] [").concat(level.toUpperCase(), "]");
    const messageStr = this.safeStringify(message);
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }
    if (args.length > 0) {
      return "".concat(prefix, " ").concat(messageStr, " ").concat(args.map(arg => this.safeStringify(arg)).join(' '));
    }
    return "".concat(prefix, " ").concat(messageStr);
  }
  debug(message) {
    if (this.shouldLog('debug')) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      console.debug(this.formatMessage('debug', message, ...args));
    }
  }
  info(message) {
    if (this.shouldLog('info')) {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      console.info(this.formatMessage('info', message, ...args));
    }
  }
  warn(message) {
    if (this.shouldLog('warn')) {
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      console.warn(this.formatMessage('warn', message, ...args));
    }
  }
  error(message) {
    if (this.shouldLog('error')) {
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }
      console.error(this.formatMessage('error', message, ...args));
    }
  }
  log(message) {
    for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
      args[_key6 - 1] = arguments[_key6];
    }
    this.info(message, ...args);
  }

  // Method to log objects in a readable format
  object(label, obj) {
    if (this.shouldLog('debug')) {
      console.group("\uD83D\uDD0D ".concat(label));
      console.log(obj);
      console.groupEnd();
    }
  }

  // Method to log arrays in a readable format
  array(label, arr) {
    if (this.shouldLog('debug')) {
      console.group("\uD83D\uDCCB ".concat(label, " (").concat(arr.length, " items)"));
      arr.forEach((item, index) => {
        console.log("[".concat(index, "]:"), item);
      });
      console.groupEnd();
    }
  }

  // Method to log performance metrics
  time(label) {
    if (this.shouldLog('debug')) {
      console.time("\u23F1\uFE0F ".concat(label));
    }
  }
  timeEnd(label) {
    if (this.shouldLog('debug')) {
      console.timeEnd("\u23F1\uFE0F ".concat(label));
    }
  }

  // Method to log table data
  table(data) {
    if (this.shouldLog('debug')) {
      console.table(data);
    }
  }

  // Method to log with custom styling (only in development)
  styled(message) {
    let style = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'color: #007acc; font-weight: bold;';
    if (this.shouldLog('info')) {
      console.log("%c".concat(this.safeStringify(message)), style);
    }
  }

  // Method to log errors with stack trace
  errorWithStack(message, error) {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message));
      if (error && error.stack) {
        console.error('Stack trace:', error.stack);
      }
    }
  }

  // Method to log API calls
  api(method, url, status, duration) {
    if (this.shouldLog('info')) {
      const statusColor = status && status >= 400 ? 'color: #ff4444' : 'color: #44ff44';
      const durationText = duration ? " (".concat(duration, "ms)") : '';
      console.log("%c".concat(method, " ").concat(url, " ").concat(status || '').concat(durationText), statusColor);
    }
  }

  // Method to log component lifecycle events
  component(componentName, event, props) {
    if (this.shouldLog('debug')) {
      console.log("\uD83C\uDFD7\uFE0F [".concat(componentName, "] ").concat(event), props || '');
    }
  }

  // Method to log state changes
  state(componentName, stateName, oldValue, newValue) {
    if (this.shouldLog('debug')) {
      console.log("\uD83D\uDD04 [".concat(componentName, "] ").concat(stateName, ":"), {
        old: oldValue,
        new: newValue
      });
    }
  }

  // Method to log user interactions
  interaction(action, details) {
    if (this.shouldLog('info')) {
      console.log("\uD83D\uDC46 ".concat(action), details || '');
    }
  }

  // Method to log navigation events
  navigation(from, to) {
    if (this.shouldLog('info')) {
      console.log("\uD83E\uDDED Navigation: ".concat(from, " \u2192 ").concat(to));
    }
  }
}

// Create a singleton instance
const logger = new Logger();

// Export the logger instance and individual methods for convenience
export default logger;

// Export individual methods for direct import
export const {
  debug,
  info,
  warn,
  error,
  log,
  object,
  array,
  time,
  timeEnd,
  table,
  styled,
  errorWithStack,
  api,
  component,
  state,
  interaction,
  navigation
} = logger;

// Export types for TypeScript